import React from 'react';
import type { DoorConfiguration, FrameProfile } from '../../types/doorConfiguration';
import {
  constrainDevelopmentDimension,
  parseDevelopmentDimension,
} from '../../pages/developmentPreviewBounds';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../ui/accordion';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../ui/tooltip';
import { ChoiceCard } from '../ChoiceCard';

const FRAME_OPTIONS: Array<{
  id: FrameProfile;
  title: string;
  description: string;
}> = [
  {
    id: 'chamfered',
    title: 'Chamfered',
    description: 'Angled inner edge with mitred corners.',
  },
  {
    id: 'sculptured',
    title: 'Sculptured',
    description: 'Ovolo inner bead with mitred corners.',
  },
  {
    id: 'butt-joint',
    title: 'Butt Joint',
    description: 'Timber profile with authored butt joints.',
  },
];

export const FrameStage: React.FC<{
  configuration: DoorConfiguration;
  widthInput: string;
  heightInput: string;
  onWidthInput: (value: string, constrain: boolean) => void;
  onHeightInput: (value: string, constrain: boolean) => void;
  onChange: (next: DoorConfiguration) => void;
}> = ({ configuration, widthInput, heightInput, onWidthInput, onHeightInput, onChange }) => (
  <div className="flex flex-col gap-4">
    <div
      role="radiogroup"
      aria-label="Frame profile"
      className="grid grid-cols-1 gap-3"
    >
      {FRAME_OPTIONS.map((option) => (
        <ChoiceCard
          key={option.id}
          title={option.title}
          description={option.description}
          selected={configuration.frameProfile === option.id}
          onSelect={() => onChange({ ...configuration, frameProfile: option.id })}
        />
      ))}
    </div>

    <Accordion type="multiple" defaultValue={['size', 'cill']} className="rounded-xl border border-border px-4">
      <AccordionItem value="size">
        <AccordionTrigger>Size</AccordionTrigger>
        <AccordionContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="flex flex-col gap-2">
              <Label htmlFor="overall-width">Overall width (mm)</Label>
              <Input
                id="overall-width"
                type="number"
                aria-label="Overall width"
                inputMode="numeric"
                value={widthInput}
                onChange={(event) => onWidthInput(event.target.value, false)}
                onBlur={(event) => onWidthInput(event.target.value, true)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="overall-height">Overall height (mm)</Label>
              <Input
                id="overall-height"
                type="number"
                aria-label="Overall height"
                inputMode="numeric"
                value={heightInput}
                onChange={(event) => onHeightInput(event.target.value, false)}
                onBlur={(event) => onHeightInput(event.target.value, true)}
              />
            </div>
          </div>
        </AccordionContent>
      </AccordionItem>
      <AccordionItem value="cill" className="border-b-0">
        <AccordionTrigger>Cill</AccordionTrigger>
        <AccordionContent>
          <div role="radiogroup" aria-label="Cill option" className="grid grid-cols-1 gap-3">
            <ChoiceCard
              title="Cill"
              description="Projects below the threshold."
              selected={configuration.ancillaries.hasCill}
              onSelect={() =>
                onChange({
                  ...configuration,
                  ancillaries: { ...configuration.ancillaries, hasCill: true, hasThreshold: true },
                })
              }
            />
            <ChoiceCard
              title="No cill"
              description="Threshold stays at the overall bottom."
              selected={!configuration.ancillaries.hasCill}
              onSelect={() =>
                onChange({
                  ...configuration,
                  ancillaries: { ...configuration.ancillaries, hasCill: false, hasThreshold: true },
                })
              }
            />
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Threshold stays included.{' '}
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" className="border-0 bg-transparent p-0 underline shadow-none">
                  Why?
                </button>
              </TooltipTrigger>
              <TooltipContent>The threshold is the normal mandatory lower-door component.</TooltipContent>
            </Tooltip>
          </p>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  </div>
);

export function commitDimension(
  raw: string,
  constrain: boolean,
  min: number,
  max: number,
  fallback: number
): number | null {
  return constrain
    ? constrainDevelopmentDimension(raw, min, max, fallback)
    : parseDevelopmentDimension(raw, min, max);
}
